/**
 * Workout history tab — list past sessions, click to open detail.
 */

import { useMemo, useState } from 'react';
import { Icon } from '@k-os/ui';
import { useWorkouts, type ExerciseType, type WorkoutSessionDto } from '../../../api/health';
import { EXERCISE_TYPE_META, StatusPill, TypeChip } from './atoms';
import styles from './workouts.module.css';

const RANGES = [
  ['7', 'Last 7 days', 7],
  ['30', 'Last 30 days', 30],
  ['90', 'Last 90 days', 90],
  ['all', 'All time', null],
] as const;

const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];

export function WorkoutHistory({
  onOpen,
}: {
  onOpen: (w: WorkoutSessionDto) => void;
}) {
  const workoutsQ = useWorkouts();
  const [range, setRange] = useState<'7' | '30' | '90' | 'all'>('30');
  const [typeFilter, setTypeFilter] = useState<ExerciseType | 'all'>('all');
  const [q, setQ] = useState('');

  // History = only concluded sessions (completed or skipped). Planned /
  // in_progress live on the Planned tab so the user can edit them ahead
  // of time. Skipped is included because it's still a closed historical
  // entry (something the user explicitly bailed on).
  const items = useMemo(
    () =>
      (workoutsQ.data?.workouts ?? []).filter(
        (w) => w.status === 'completed' || w.status === 'skipped',
      ),
    [workoutsQ.data],
  );

  const filtered = useMemo(() => {
    const cutoff =
      range === 'all'
        ? null
        : Date.now() - Number(range) * 24 * 60 * 60 * 1000;
    const lower = q.trim().toLowerCase();
    return items.filter((w) => {
      if (cutoff && new Date(w.date).getTime() < cutoff) return false;
      if (typeFilter !== 'all' && w.type !== typeFilter) return false;
      if (lower && !w.name.toLowerCase().includes(lower)) return false;
      return true;
    });
  }, [items, range, typeFilter, q]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search history…"
          className={styles.searchField}
        />
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          {filtered.length} of {items.length}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>
          Range
        </span>
        {RANGES.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`${styles.filterChip} ${range === id ? styles.filterChipActive : ''}`}
            onClick={() => setRange(id as typeof range)}
          >
            {label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4 }}>
          Type
        </span>
        <button
          type="button"
          className={`${styles.filterChip} ${typeFilter === 'all' ? styles.filterChipActive : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          All
        </button>
        {TYPE_KEYS.map((t) => (
          <button
            key={t}
            type="button"
            className={`${styles.filterChip} ${typeFilter === t ? styles.filterChipActive : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            <span style={{ width: 7, height: 7, borderRadius: 2, background: EXERCISE_TYPE_META[t].color, marginRight: 4 }} />
            {EXERCISE_TYPE_META[t].name}
          </button>
        ))}
      </div>

      {workoutsQ.isLoading ? (
        <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--ink-3)',
            border: '1px dashed var(--line)',
            borderRadius: 8,
            background: 'var(--surface-2)',
          }}
        >
          No workouts in this window.
        </div>
      ) : (
        <div>
          {filtered.map((w) => (
            <div key={w.id} className={styles.histRow} onClick={() => onOpen(w)}>
              <div className={styles.histDate}>
                <div className={styles.histDay}>{formatDate(w.date)}</div>
                <div className={styles.histTime}>
                  {w.startTime ? new Date(w.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </div>
              </div>
              <div>
                <div className={styles.histName}>{w.name}</div>
                <div className={styles.histMeta}>
                  {isExerciseType(w.type) && <TypeChip type={w.type} />}
                  {w.status === 'completed' && w.durationMin && (
                    <span>{w.durationMin} min</span>
                  )}
                  {w.difficultyAvg != null && (
                    <span>· avg difficulty {w.difficultyAvg}/5</span>
                  )}
                  {w.rpe != null && <span>· RPE {w.rpe}</span>}
                </div>
              </div>
              <div className={styles.histRight}>
                <StatusPill status={w.status} />
                <Icon name="arrowRight" size={12} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function isExerciseType(v: string): v is ExerciseType {
  return TYPE_KEYS.includes(v as ExerciseType);
}
